/**
 * UWWorkbook.jsx — Main page
 * Excel-like underwriting workbook with tabbed navigation.
 * Tabs: DSCR Analysis · Debt Schedule · Global DSCR · PFS & DTI
 * + placeholder stubs for remaining tabs.
 *
 * Features:
 *  - Auto-save with debounced API calls
 *  - Live DSCR calculation summary in header
 *  - "Banker View" toggle to explain each metric in plain language
 *  - Extract from Tax Return (OCR) button
 *  - Submit for review
 *  - Professional fintech styling (Tailwind + shadcn/ui)
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { cn, formatCurrency, formatMultiple, calcDSCR, dscrColor, dscrBadge, dscrLabel } from "../../lib/utils";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Save, Send, FileText, Download, RefreshCw, Info,
  CheckCircle, Clock, AlertTriangle, ChevronRight,
  Building2, FileSpreadsheet, BarChart3, User, Globe,
  CreditCard, Layers, Shield, Loader2
} from "lucide-react";

import UWDSCRTab       from "./UWDSCRTab";
import UWDebtScheduleTab from "./UWDebtScheduleTab";
import UWGlobalDSCRTab from "./UWGlobalDSCRTab";
import UWPFSDTITab     from "./UWPFSDTITab";
import UWCollateralTab from "./UWCollateralTab";

const API = import.meta.env.VITE_API_URL || "http://localhost:5000";

// ── Placeholder stub for tabs not yet built ───────────────────────────────
function ComingSoon({ tabName }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 text-gray-400 p-12">
      <FileSpreadsheet size={48} className="opacity-30" />
      <div className="text-lg font-semibold text-gray-500">{tabName}</div>
      <p className="text-sm text-center max-w-sm">
        This tab is coming soon. Data from your application and uploaded documents will be
        automatically populated here.
      </p>
    </div>
  );
}

// ── Tab definitions (matching Excel tab bar) ──────────────────────────────
const TABS = [
  { id: "dscr",         label: "(UW) DSCR",             icon: BarChart3,    color: "orange", component: "dscr" },
  { id: "debtSchedule", label: "Debt Schedule (Borrower)", icon: FileText,   color: "orange", component: "debtSchedule" },
  { id: "globalDSCR",   label: "Global DSCR",            icon: Globe,        color: "orange", component: "globalDSCR" },
  { id: "pfs",          label: "PFS & DTI",              icon: User,         color: "purple", component: "pfs" },
  { id: "collateral",   label: "Collateral",             icon: Building2,    color: "blue",   component: "collateral" },
  { id: "loanExposure", label: "Loan Exposure",          icon: CreditCard,   color: "blue",   component: "stub" },
  { id: "creditMemo",   label: "Credit Memo",            icon: FileText,     color: "green",  component: "stub" },
  { id: "closingCond",  label: "Closing Conditions",     icon: Shield,       color: "blue",   component: "stub" },
];

// ── Status indicator ──────────────────────────────────────────────────────
function SaveStatus({ status }) {
  return (
    <div className={cn("flex items-center gap-1.5 text-xs px-3 py-1 rounded-full transition-all",
      status === "saved"   && "bg-green-50 text-green-700",
      status === "saving"  && "bg-blue-50  text-blue-700",
      status === "unsaved" && "bg-yellow-50 text-yellow-700",
      status === "error"   && "bg-red-50   text-red-700",
    )}>
      {status === "saved"   && <><CheckCircle size={12} /> Saved</>}
      {status === "saving"  && <><Loader2 size={12} className="animate-spin" /> Saving…</>}
      {status === "unsaved" && <><Clock size={12} /> Unsaved changes</>}
      {status === "error"   && <><AlertTriangle size={12} /> Save failed</>}
    </div>
  );
}

// ── DSCR Summary chips ─────────────────────────────────────────────────────
function DSCRSummaryChip({ label, dscr }) {
  if (dscr === null || dscr === undefined) return null;
  return (
    <div className="flex flex-col items-center px-3 py-1.5 bg-white rounded-lg border border-gray-200 shadow-sm min-w-[90px]">
      <span className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">{label}</span>
      <span className={cn("text-base font-bold font-mono", dscrColor(dscr))}>
        {formatMultiple(dscr)}
      </span>
      <span className={cn("text-[9px] font-semibold mt-0.5",
        dscr >= 1.25 ? "text-green-600" : dscr >= 1.0 ? "text-yellow-600" : "text-red-600"
      )}>
        {dscrLabel(dscr)}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function UWWorkbook() {
  const [workbook, setWorkbook] = useState(null);
  const [activeTab, setActiveTab]   = useState("dscr");
  const [saveStatus, setSaveStatus] = useState("saved");
  const [bankerView, setBankerView] = useState(false);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const saveTimer = useRef(null);

  // ── Load workbook ──────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        const res   = await axios.get(`${API}/api/uw-workbook`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWorkbook(res.data.data);
      } catch (err) {
        setError("Failed to load workbook. Please refresh.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ── Debounced auto-save ────────────────────────────────────────────────
  const autoSave = useCallback((patch) => {
    if (!workbook?._id) return;
    setSaveStatus("unsaved");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaveStatus("saving");
      try {
        const token = localStorage.getItem("token");
        await axios.put(`${API}/api/uw-workbook/${workbook._id}`, patch, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSaveStatus("saved");
      } catch {
        setSaveStatus("error");
      }
    }, 1500);
  }, [workbook]);

  // ── Tab data change handlers ───────────────────────────────────────────
  const handleDSCRChange = useCallback((data) => {
    setWorkbook(prev => ({ ...prev, dscrPeriods: data.periods }));
    autoSave({ dscrPeriods: data.periods });
  }, [autoSave]);

  const handleDebtChange = useCallback((data) => {
    setWorkbook(prev => ({
      ...prev,
      debtScheduleExisting: data.existing,
      debtScheduleProposed: data.proposed,
      debtScheduleComments: data.comments,
    }));
    autoSave({
      debtScheduleExisting: data.existing,
      debtScheduleProposed: data.proposed,
      debtScheduleComments: data.comments,
    });
  }, [autoSave]);

  const handleGlobalChange = useCallback((data) => {
    setWorkbook(prev => ({ ...prev, globalPeriods: data.periods }));
    autoSave({ globalPeriods: data.periods });
  }, [autoSave]);

  const handlePFSChange = useCallback((data) => {
    setWorkbook(prev => ({ ...prev, ...data }));
    autoSave(data);
  }, [autoSave]);

  const handleCollateralChange = useCallback((data) => {
    setWorkbook(prev => ({ ...prev, ...data }));
    autoSave(data);
  }, [autoSave]);

  // ── Manual save ───────────────────────────────────────────────────────
  const handleManualSave = async () => {
    if (!workbook?._id) return;
    setSaveStatus("saving");
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/api/uw-workbook/${workbook._id}`, workbook, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaveStatus("saved");
    } catch {
      setSaveStatus("error");
    }
  };

  // ── Submit for review ─────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!workbook?._id) return;
    if (!window.confirm("Submit this workbook for lender review?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/api/uw-workbook/${workbook._id}/submit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkbook(prev => ({ ...prev, status: 'in_review' }));
    } catch {
      alert("Failed to submit. Please try again.");
    }
  };

  // ── Compute live DSCR summary for header ──────────────────────────────
  const dscrSummary = (() => {
    if (!workbook?.dscrPeriods?.length) return [];
    return workbook.dscrPeriods.slice(0, 4).map((p, i) => {
      const cf =
        (p.netIncome || 0) + (p.interestAddback || 0) + (p.depreciationAddback || 0) +
        (p.rentAddback || 0) + (p.ownersCompensation || 0) -
        (p.salaryRequired || 0) - (p.rentalIncome || 0) - (p.distributions || 0) +
        (p.excessDistributions || 0) + (p.lossOnAssets || 0) + (p.otherAddback || 0);
      const ds =
        (p.sevenASmallLoan || 0) + (p.usdaBnI || 0) + (p.fiveFourSeniorFB || 0) +
        (p.fiveFourSubordinateCDC || 0) + (p.sellersOtherNotes || 0);
      return { label: p.date || `Period ${i + 1}`, dscr: ds > 0 ? calcDSCR(cf, ds) : null };
    }).filter(d => d.dscr !== null);
  })();

  // ── Render ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center space-y-3">
          <Loader2 size={36} className="animate-spin text-[#1e3a5f] mx-auto" />
          <p className="text-sm text-gray-500 font-medium">Loading Underwriting Workbook…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center space-y-3 text-red-500">
          <AlertTriangle size={36} className="mx-auto" />
          <p className="text-sm font-medium">{error}</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            <RefreshCw size={14} className="mr-2" /> Reload
          </Button>
        </div>
      </div>
    );
  }

  const activeTabConfig = TABS.find(t => t.id === activeTab);

  return (
    <div className="flex flex-col h-screen bg-gray-100 overflow-hidden">
      {/* ── Top header bar ── */}
      <div className="bg-[#1e3a5f] text-white px-4 py-2.5 flex items-center gap-4 shrink-0 shadow-md">
        <div className="flex items-center gap-2.5">
          <FileSpreadsheet size={20} className="text-blue-300" />
          <div>
            <div className="text-sm font-bold leading-tight">Underwriting Workbook</div>
            <div className="text-[10px] text-blue-300 leading-tight">
              {workbook?.loanProgram?.toUpperCase() || "SBA 7(a)"} · {workbook?.status === 'in_review' ? '🔍 In Review' : 'Draft'}
            </div>
          </div>
        </div>

        {/* Borrower name */}
        <div className="text-sm text-blue-200 border-l border-blue-700 pl-4">
          <span className="text-blue-400 text-[10px] uppercase tracking-wide block">Borrower</span>
          <span className="font-semibold">{workbook?.borrowerName || "—"}</span>
        </div>

        {/* Live DSCR chips */}
        {dscrSummary.length > 0 && (
          <div className="flex items-center gap-2 border-l border-blue-700 pl-4">
            <span className="text-[10px] text-blue-400 uppercase tracking-wide mr-1">DSCR</span>
            {dscrSummary.map((d, i) => (
              <DSCRSummaryChip key={i} label={d.label} dscr={d.dscr} />
            ))}
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Banker view toggle */}
        <button
          onClick={() => setBankerView(v => !v)}
          className={cn(
            "flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border transition-colors",
            bankerView ? "bg-blue-600 border-blue-500 text-white" : "border-blue-600 text-blue-300 hover:bg-blue-800"
          )}
        >
          <Info size={13} />
          {bankerView ? "Banker View ON" : "Banker View"}
        </button>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <SaveStatus status={saveStatus} />
          <Button size="sm" variant="ghost" onClick={handleManualSave}
            className="text-white hover:bg-blue-800 hover:text-white gap-1.5">
            <Save size={14} /> Save
          </Button>
          <Button size="sm" variant="ghost"
            className="text-white hover:bg-blue-800 hover:text-white gap-1.5">
            <Download size={14} /> Export
          </Button>
          {workbook?.status === 'draft' && (
            <Button size="sm" variant="success" onClick={handleSubmit}
              className="bg-green-600 hover:bg-green-700 text-white gap-1.5">
              <Send size={14} /> Submit for Review
            </Button>
          )}
          {workbook?.status === 'in_review' && (
            <Badge variant="info" className="text-xs py-1 px-3">Under Review</Badge>
          )}
          {workbook?.status === 'approved' && (
            <Badge variant="success" className="text-xs py-1 px-3">✓ Approved</Badge>
          )}
        </div>
      </div>

      {/* ── Banker view explanation bar (when toggled) ── */}
      {bankerView && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 text-xs text-blue-800 shrink-0">
          <span className="font-bold">Banker View:</span> This workbook shows your finances exactly how a banker evaluates them.
          A DSCR ≥ 1.25x means your business generates enough cash to service debt + 25% buffer.
          {activeTab === "dscr" && " The DSCR tab calculates your business cash flow across historical years and projections."}
          {activeTab === "debtSchedule" && " The Debt Schedule lists all existing and proposed obligations. Every payment affects your DSCR."}
          {activeTab === "globalDSCR" && " Global DSCR combines personal + business cash flows. SBA requires this for full picture underwriting."}
          {activeTab === "pfs" && " The PFS is your personal net worth statement – required for all SBA guarantors."}
        </div>
      )}

      {/* ── Tab bar (Excel-style) ── */}
      <div className="bg-gray-100 border-b-2 border-[#1e3a5f] shrink-0 overflow-x-auto">
        <div className="flex gap-0.5 px-2 pt-1.5">
          {/* Overview summary mini-tab */}
          <button
            onClick={() => setActiveTab("overview")}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold whitespace-nowrap rounded-t-md border border-b-0 transition-all",
              activeTab === "overview"
                ? "bg-[#1e3a5f] text-white border-[#1e3a5f] shadow-sm"
                : "bg-[#c6efce] text-[#276221] border-[#c6efce] hover:bg-[#b0e6b8]"
            )}
          >
            <Building2 size={11} />
            Overview
          </button>

          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const colorMap = {
              orange: { active: "bg-[#fce4d6] text-[#833b00] border-[#f4b183]", inactive: "bg-[#fce4d6] text-[#833b00] border-[#f4b183] hover:bg-[#f8d0b8] opacity-75" },
              blue:   { active: "bg-[#dce6f1] text-[#17375e] border-[#9dc3e6]", inactive: "bg-[#dce6f1] text-[#17375e] border-[#9dc3e6] hover:bg-[#c9d9ea] opacity-75" },
              purple: { active: "bg-[#e2d0f0] text-[#3d1357] border-[#b4a0c8]", inactive: "bg-[#e2d0f0] text-[#3d1357] border-[#b4a0c8] hover:bg-[#d5c0e8] opacity-75" },
              green:  { active: "bg-[#c6efce] text-[#276221] border-[#92d051]", inactive: "bg-[#c6efce] text-[#276221] border-[#92d051] hover:bg-[#b0e6b8] opacity-75" },
            };
            const colors = colorMap[tab.color] || colorMap.blue;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold whitespace-nowrap rounded-t-md border border-b-0 transition-all",
                  isActive
                    ? cn(colors.active, "shadow-sm -mb-0.5 pb-2")
                    : colors.inactive
                )}
              >
                <Icon size={11} />
                {tab.label}
                {tab.component === "stub" && (
                  <span className="text-[8px] opacity-50 ml-0.5">soon</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Tab content area ── */}
      <div className="flex-1 overflow-hidden bg-white">
        {/* Overview */}
        {activeTab === "overview" && (
          <div className="h-full overflow-auto p-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-1">Loan Application Overview</h2>
              <p className="text-sm text-gray-500 mb-6">
                Your financial data is organized below exactly the way your banker will review it.
                Complete all highlighted sections for the fastest approval.
              </p>

              <div className="grid grid-cols-2 gap-6">
                {/* DSCR summary */}
                <div className="border border-gray-200 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-[#1e3a5f]">DSCR Summary</h3>
                    <button onClick={() => setActiveTab("dscr")} className="text-xs text-blue-600 hover:underline flex items-center gap-1">Open <ChevronRight size={12} /></button>
                  </div>
                  {dscrSummary.length === 0 ? (
                    <div className="text-sm text-gray-400 italic py-4 text-center">
                      No data yet — complete the DSCR tab to see your coverage ratios
                    </div>
                  ) : (
                    <div className="flex gap-3 flex-wrap">
                      {dscrSummary.map((d, i) => <DSCRSummaryChip key={i} label={d.label} dscr={d.dscr} />)}
                    </div>
                  )}
                </div>

                {/* Completion checklist */}
                <div className="border border-gray-200 rounded-xl p-4 shadow-sm">
                  <h3 className="text-sm font-bold text-[#1e3a5f] mb-3">Completion Checklist</h3>
                  <div className="space-y-2">
                    {[
                      { label: "DSCR Analysis",       done: (workbook?.dscrPeriods?.length > 0) },
                      { label: "Debt Schedule",        done: (workbook?.debtScheduleExisting?.length > 0) },
                      { label: "Global DSCR",          done: (workbook?.globalPeriods?.length > 0) },
                      { label: "PFS & DTI",            done: !!workbook?.pfsGuarantorName },
                      { label: "Tax Returns Uploaded", done: workbook?.taxReturnExtracted || false },
                    ].map(({ label, done }) => (
                      <div key={label} className="flex items-center gap-2 text-sm">
                        {done
                          ? <CheckCircle size={15} className="text-green-600 shrink-0" />
                          : <div className="w-[15px] h-[15px] rounded-full border-2 border-gray-300 shrink-0" />
                        }
                        <span className={done ? "text-gray-700" : "text-gray-400"}>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* What bankers look for */}
                <div className="col-span-2 border border-blue-100 rounded-xl p-4 bg-blue-50">
                  <h3 className="text-sm font-bold text-[#1e3a5f] mb-2 flex items-center gap-2">
                    <Info size={14} className="text-blue-500" /> What Bankers Look For
                  </h3>
                  <div className="grid grid-cols-3 gap-4 text-xs text-gray-700">
                    {[
                      { metric: "DSCR ≥ 1.25x", desc: "Business generates 25% more cash than needed for debt payments" },
                      { metric: "Credit ≥ 680", desc: "Strong personal credit history demonstrates reliability" },
                      { metric: "Net Worth Positive", desc: "Your assets exceed your liabilities — skin in the game" },
                      { metric: "Liquidity Post-Close", desc: "Minimum 3-6 months of payments in reserves after loan closes" },
                      { metric: "DTI ≤ 45%", desc: "Personal debt-to-income ratio stays manageable" },
                      { metric: "Stable Revenue", desc: "2+ years of consistent or growing business revenue" },
                    ].map(({ metric, desc }) => (
                      <div key={metric} className="flex gap-2">
                        <CheckCircle size={13} className="text-green-600 mt-0.5 shrink-0" />
                        <div>
                          <div className="font-semibold text-[#1e3a5f]">{metric}</div>
                          <div className="text-[10px] text-gray-500 mt-0.5">{desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* DSCR Tab */}
        {activeTab === "dscr" && (
          <UWDSCRTab
            initialData={{ periods: workbook?.dscrPeriods }}
            borrowerName={workbook?.borrowerName}
            onDataChange={handleDSCRChange}
          />
        )}

        {/* Debt Schedule Tab */}
        {activeTab === "debtSchedule" && (
          <UWDebtScheduleTab
            initialData={{
              existing: workbook?.debtScheduleExisting,
              proposed: workbook?.debtScheduleProposed,
              comments: workbook?.debtScheduleComments,
            }}
            borrowerName={workbook?.borrowerName}
            onDataChange={handleDebtChange}
          />
        )}

        {/* Global DSCR Tab */}
        {activeTab === "globalDSCR" && (
          <UWGlobalDSCRTab
            initialData={{ periods: workbook?.globalPeriods }}
            borrowerName={workbook?.borrowerName}
            onDataChange={handleGlobalChange}
          />
        )}

        {/* PFS & DTI Tab */}
        {activeTab === "pfs" && (
          <UWPFSDTITab
            initialData={{
              assets:      workbook?.pfsAssets,
              liabilities: workbook?.pfsLiabilities,
              credit:      workbook?.pfsCredit,
              income:      workbook?.pfsIncome,
            }}
            borrowerName={workbook?.pfsGuarantorName || workbook?.borrowerName}
            onDataChange={handlePFSChange}
          />
        )}

        {/* Stub tabs */}
        {/* Collateral Tab */}
        {activeTab === "collateral" && (
          <UWCollateralTab
            initialData={{
              collateralItems: workbook?.collateralItems,
              proposedLoanAmount: workbook?.collateralProposedLoanAmount,
              uccSearchDate: workbook?.collateralUCCSearchDate,
              uccEntries: workbook?.collateralUCCEntries,
              sba504ProposedBankLoan: workbook?.collateral504ProposedBankLoan,
            }}
            borrowerName={workbook?.borrowerName}
            onDataChange={handleCollateralChange}
          />
        )}

        {/* Stub tabs */}
        {["loanExposure","creditMemo","closingCond"].includes(activeTab) && (
          <ComingSoon tabName={TABS.find(t => t.id === activeTab)?.label} />
        )}
      </div>

      {/* ── Bottom status bar ── */}
      <div className="bg-[#1e3a5f] text-blue-300 text-[10px] px-4 py-1 flex items-center gap-4 shrink-0">
        <span>CoreLoanHub UW Workbook v1.0</span>
        <span className="opacity-50">·</span>
        <span>Last saved: {workbook?.lastSavedAt ? new Date(workbook.lastSavedAt).toLocaleTimeString() : "—"}</span>
        <span className="opacity-50">·</span>
        <span className={cn(
          workbook?.status === 'draft' ? "text-yellow-400" :
          workbook?.status === 'in_review' ? "text-blue-300" :
          workbook?.status === 'approved' ? "text-green-400" : ""
        )}>
          Status: {workbook?.status?.replace('_', ' ').toUpperCase() || "DRAFT"}
        </span>
        <div className="flex-1" />
        <span>SBA 7(a) · SBA 504 · USDA B&I · Conventional</span>
      </div>
    </div>
  );
}
